import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React, { useState } from 'react'
import { LoadingButton } from '@mui/lab'
import {
  AdoptionAttemptDocumentDto,
  AdoptionAttemptDocumentDtoType,
  AdoptionAttemptDto,
} from '../../../interactors/gen/backendClient'
import { ArrowDownward } from '@mui/icons-material'
import { theme } from '../../theme'
import dayjs from 'dayjs'
import { useMutation } from 'react-query'
import { AdoptersClient } from '../../../interactors/clients/AdoptersClient'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { getAdoptionAttemptDocumentsSrc } from '../../../utils/S3-links'

interface Props {
  adoptionAttempt: AdoptionAttemptDto
  adopterId: string
  isReader: boolean
  defaultExpanded?: boolean
}

export const ExitDocumentsCard: React.FC<Props> = ({
  adoptionAttempt,
  adopterId,
  isReader,
  defaultExpanded = false,
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [expanded, setExpanded] = useState(defaultExpanded)

  const handleToggleExpand = () => {
    setExpanded(!expanded)
  }

  const documents = adoptionAttempt?.documents?.sort((a, b) => dayjs(b.uploadedAt).diff(dayjs(a.uploadedAt)))
  const documentsByType = (documents || []).reduce<Record<string, AdoptionAttemptDocumentDto>>((acc, document) => {
    const existing = acc[document.type]
    if (!existing || dayjs(document.uploadedAt).isAfter(dayjs(existing.uploadedAt))) {
      acc[document.type] = document
    }
    return acc
  }, {})

  const documentRows: { title: string; type: AdoptionAttemptDocumentDtoType }[] = [
    { title: 'Attestation de cession', type: AdoptionAttemptDocumentDtoType.SellCertificate },
    { title: 'Certificat vétérinaire', type: AdoptionAttemptDocumentDtoType.VeterinaryCertificate },
    { title: "Document d'information", type: AdoptionAttemptDocumentDtoType.InformationDocument },
    { title: 'Carnet de santé', type: AdoptionAttemptDocumentDtoType.HealthBook },
    { title: 'Certificat de stérilisation', type: AdoptionAttemptDocumentDtoType.SterilizationCertificate },
    { title: 'Autre document', type: AdoptionAttemptDocumentDtoType.Others },
  ]

  return (
    <Card
      sx={{
        m: 0,
        width: '100%',
      }}
    >
      <CardContent
        sx={{
          m: 0,
          p: isMobile ? 'auto' : '12px !important',
          '&:last-child': { pb: isMobile ? 0 : 'auto' },
        }}
      >
        <Box
          onClick={handleToggleExpand}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Typography variant="h4" component="p" sx={{ fontSize: isMobile ? 18 : 20, fontWeight: 700 }}>
            {isMobile ? 'Documents obligatoires' : 'Mise à disposition des documents obligatoires'}
          </Typography>
          <IconButton>
            <ArrowDownward
              style={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
              }}
            />
          </IconButton>
        </Box>
        <Collapse in={expanded} timeout="auto">
          <TableContainer sx={{ mt: 1 }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documentRows.map((row) => {
                  const document = documentsByType[row.type]
                  return (
                    <ExitDocumentRow
                      key={row.type}
                      title={row.title}
                      type={row.type}
                      adopterId={adopterId}
                      adoptionAttemptId={adoptionAttempt.id}
                      documentKey={document?.documentKey}
                      uploadedAt={document?.uploadedAt}
                      disabled={isReader}
                    />
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </CardContent>
    </Card>
  )
}

interface ExitDocumentRowProps {
  title: string
  type: AdoptionAttemptDocumentDtoType
  adopterId: string
  adoptionAttemptId: string
  documentKey?: string
  uploadedAt?: string
  disabled?: boolean
}

const ExitDocumentRow: React.FC<ExitDocumentRowProps> = ({
  title,
  type,
  adopterId,
  adoptionAttemptId,
  documentKey,
  uploadedAt,
  disabled = false,
}) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [fileKey, setFileKey] = React.useState<string | undefined>(documentKey)
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const globalSnackbar = useGlobalSnackbarStore()

  React.useEffect(() => {
    setFileKey(documentKey)
  }, [documentKey])

  const uploadFileMutation = useMutation(
    async ({ file }: { file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', type)

      return await AdoptersClient.addDocumentToAdoptionAttempt(adopterId, adoptionAttemptId, formData)
    },
    {
      onSuccess: (data) => {
        setFileKey(data)
        setSelectedFile(null)
        if (inputRef.current) {
          inputRef.current.value = ''
        }
        globalSnackbar.triggerSuccessMessage(`Votre document a bien été ajouté !`)
      },
    }
  )

  const statusLabel = !fileKey
    ? 'Manquant'
    : fileKey === 'NOT_PROVIDED.pdf'
    ? 'Remis en mains propres'
    : uploadedAt
    ? `Ajouté le ${dayjs(uploadedAt).format('DD/MM/YYYY')}`
    : 'Disponible'

  const actionLabel = fileKey === 'NOT_PROVIDED.pdf' || !fileKey ? 'Ajouter' : 'Remplacer'

  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600 }}>{title}</TableCell>
      <TableCell>{statusLabel}</TableCell>
      <TableCell align="right">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
          <Button component="label" variant="outlined" size="small" disabled={disabled} sx={{ textTransform: 'none' }}>
            {actionLabel}
            <input
              ref={inputRef}
              type="file"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0]
                setSelectedFile(file ?? null)
              }}
            />
          </Button>
          {selectedFile && (
            <LoadingButton
              variant="contained"
              size="small"
              loading={uploadFileMutation.isLoading}
              disabled={disabled}
              onClick={() => {
                if (selectedFile) {
                  uploadFileMutation.mutate({ file: selectedFile })
                }
              }}
              sx={{ textTransform: 'none' }}
            >
              Envoyer
            </LoadingButton>
          )}
          {fileKey && fileKey !== 'NOT_PROVIDED.pdf' && (
            <Button
              variant="contained"
              size="small"
              sx={{ textTransform: 'none' }}
              href={getAdoptionAttemptDocumentsSrc(fileKey)}
              target="_blank"
              rel="noopener noreferrer"
              disabled={disabled}
            >
              Consulter
            </Button>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  )
}
